import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  formatSpecialtyLabel,
  formatSpecialtyRemainder,
  formatWeeklyBreakSummary,
  formatWeeklyRoutineSummary,
  formatWeeklyWorkStretchSummary,
  isSpecialtyUnmarked,
  RosterScheduleSlot,
  RosterService,
} from "./professionalRosterSummary";

type ProfessionalRosterLinesProps = {
  services: RosterService[] | null | undefined;
  schedules: RosterScheduleSlot[] | null | undefined;
  restrictProfessionalServices: boolean;
  onMarkServices?: () => void;
};

const ProfessionalRosterLines = ({
  services,
  schedules,
  restrictProfessionalServices,
  onMarkServices,
}: ProfessionalRosterLinesProps) => {
  const { t } = useTranslation();
  const [remainderOpen, setRemainderOpen] = useState(false);
  const specialty = formatSpecialtyLabel(services, restrictProfessionalServices, t);
  const remainder = formatSpecialtyRemainder(services, restrictProfessionalServices, t);
  const unmarked = isSpecialtyUnmarked(services, restrictProfessionalServices);
  const hours = formatWeeklyRoutineSummary(schedules, t);
  const breaks = formatWeeklyBreakSummary(schedules, t);
  const workStretches = formatWeeklyWorkStretchSummary(schedules, t);
  const specialtyClass = unmarked
    ? "small mt-1 text-warning fw-semibold"
    : "text-muted small mt-1";
  const markedCount = (services ?? []).filter((service) => service.name.trim().length > 0).length;
  const showAdjust = Boolean(
    restrictProfessionalServices && onMarkServices && !unmarked && markedCount > 0
  );

  return (
    <>
      {remainder ? (
        <button
          type="button"
          className={`btn btn-link p-0 text-start align-baseline ${specialtyClass}`}
          style={{ textDecoration: "none" }}
          title={remainder}
          aria-expanded={remainderOpen}
          onClick={() => setRemainderOpen((open) => !open)}
        >
          {specialty}
          <span className="visually-hidden"> {remainder}</span>
        </button>
      ) : (
        <div className={specialtyClass}>
          {specialty}
          {unmarked && onMarkServices ? (
            <>
              {" "}
              <button
                type="button"
                className="btn btn-link btn-sm p-0 align-baseline text-warning fw-semibold"
                onClick={onMarkServices}
              >
                {t("studio.professionals.specialty.markNow")}
              </button>
            </>
          ) : null}
        </div>
      )}
      {showAdjust ? (
        <div>
          <button
            type="button"
            className="btn btn-link btn-sm p-0 align-baseline text-muted"
            onClick={onMarkServices}
          >
            {t("studio.professionals.specialty.adjustCounted", { count: markedCount })}
          </button>
        </div>
      ) : null}
      {remainderOpen && remainder ? (
        <div className="text-muted small">{remainder}</div>
      ) : null}
      <div className="text-muted small">{hours}</div>
      {breaks ? <div className="text-muted small">{breaks}</div> : null}
      {workStretches ? (
        <div className="text-muted small">{workStretches}</div>
      ) : null}
    </>
  );
};

export default ProfessionalRosterLines;
